/**
 * Playful names for bots (mahjong tables, Dou Dizhu, training, tournaments, the house bots of Pot and Red Envelopes): two parts in the viewer's language
 * («摸鱼熊猫», «Spicy Dumpling», «Graf Knödel», «Граф Пельмень»). Deterministic from the table and the bot's number, so
 * everybody at the table sees the same name and the bots of one table never share one. Friendly only: no mockery, no
 * money or luck-to-win hints, no real people. Bots stay marked as bots wherever the name is shown (badge or «· bot»).
 */

interface Parts {
  /** first part (adjective / title) */
  a: readonly string[];
  /** second part (noun) */
  b: readonly string[];
  join: (a: string, b: string) => string;
}

const PARTS: Record<'en' | 'de' | 'zh' | 'ru', Parts> = {
  // zh: no 四 / 死, no brands, nothing religious or political, no luck-to-win symbols (锦鲤, 财神 …)
  zh: {
    a: ['摸鱼', '呆萌', '快乐', '淡定', '迷糊', '元气', '养生', '话痨', '爱笑', '温柔', '好奇', '悠闲', '贪吃', '打盹', '机灵', '慢吞吞'],
    b: ['熊猫', '饺子', '包子', '糯米', '奶茶', '汤圆', '月饼', '小龙', '茶叶蛋', '豆腐', '柿子', '小熊', '年糕', '灯笼', '橘猫', '煎饼'],
    join: (a, b) => a + b,
  },
  en: {
    a: ['Sleepy', 'Spicy', 'Chill', 'Fluffy', 'Jolly', 'Wobbly', 'Cosmic', 'Crispy', 'Humble', 'Curious', 'Dizzy', 'Mighty', 'Snoozy', 'Bouncy', 'Giggly', 'Toasty'],
    b: ['Dumpling', 'Panda', 'Teapot', 'Noodle', 'Koi', 'Bao', 'Mooncake', 'Pickle', 'Tofu', 'Lantern', 'Dragon', 'Biscuit', 'Waffle', 'Penguin', 'Muffin', 'Kettle'],
    join: (a, b) => `${a} ${b}`,
  },
  de: {
    a: ['Kapitän', 'Professor', 'Onkel', 'Tante', 'Graf', 'Meister', 'Doktor', 'Baron', 'Opa', 'Käpt’n', 'Sir', 'Chef', 'Oma', 'Herzog', 'Madame', 'Kommissar'],
    b: ['Knödel', 'Panda', 'Nudel', 'Tofu', 'Laterne', 'Mondkuchen', 'Teebeutel', 'Gurke', 'Keks', 'Drache', 'Karpfen', 'Wolke', 'Brezel', 'Pinguin', 'Waffel', 'Igel'],
    join: (a, b) => `${a} ${b}`,
  },
  ru: {
    a: ['Капитан', 'Профессор', 'Дядя', 'Тётя', 'Граф', 'Мастер', 'Доктор', 'Барон', 'Шеф', 'Сэр', 'Дедушка', 'Маэстро', 'Бабушка', 'Герцог', 'Мадам', 'Синьор'],
    b: ['Пельмень', 'Панда', 'Лапша', 'Тофу', 'Фонарик', 'Пряник', 'Чайник', 'Огурчик', 'Карп', 'Дракончик', 'Пирожок', 'Мандарин', 'Блинчик', 'Пингвин', 'Ёжик', 'Вареник'],
    join: (a, b) => `${a} ${b}`,
  },
};

/** for tests: the word lists */
export const BOT_NAME_PARTS: Readonly<Record<string, { a: readonly string[]; b: readonly string[] }>> = PARTS;

/** candidate steps over the a×b grid; only those coprime to the grid size are used (distinct names for n < size) */
const STEPS = [5, 7, 11, 13, 17, 19, 23, 25, 29, 31, 35, 37, 41, 43, 47, 49, 53, 55] as const;

const gcd = (x: number, y: number): number => (y === 0 ? x : gcd(y, x % y));

/** FNV-1a 32-bit */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * The name of bot number `n` (1-based) at table `seed` in `locale`. Bots of one table get distinct names: the index
 * walks the a×b grid with a step coprime to its size from a table-dependent start.
 */
export function botName(seed: string, n: number, locale: string): string {
  const lang = (locale.slice(0, 2) as keyof typeof PARTS) in PARTS ? (locale.slice(0, 2) as keyof typeof PARTS) : 'en';
  const p = PARTS[lang];
  const size = p.a.length * p.b.length;
  // a table-dependent step coprime to the grid size: distinct names for every n < size, and varied from table to table
  const steps = STEPS.filter((x) => gcd(x, size) === 1);
  const step = steps[hash(`${seed}#`) % steps.length] ?? 1;
  const k = (hash(seed) + n * step) % size;
  return p.join(p.a[Math.floor(k / p.b.length)] ?? '', p.b[k % p.b.length] ?? '');
}

/**
 * Seeds of the house bots of shared rounds (docs/superpowers/specs/2026-10-01-house-bots-design.md): one bot per round,
 * named from the round so every viewer — the game page, the lobby, the round history — sees the same name.
 * Pot by the round number (the lobby feed carries no round id), Red Envelopes by the round id.
 */
export const potBotSeed = (roundNo: number): string => `pot#${roundNo}`;
export const hongbaoBotSeed = (roundId: string): string => `round:${roundId}`;
